import React from 'react'
import { Link as GatsbyLink } from 'gatsby'
import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
} from '@chakra-ui/react'

import { AuthErrorAction, AuthErrorInfo } from '~utils/auth-errors'

interface Props {
    info: AuthErrorInfo | undefined
}

/**
 * Prominent inline error block for auth flows (login, password reset).
 * Renders a clear title + explanation + up to two suggested next steps so
 * the user always knows what to do.
 */
export default function AuthErrorAlert({ info }: Props) {
    if (!info) return null

    const { title, description, primaryAction, secondaryAction } = info

    return (
        <Alert
            status="error"
            variant="left-accent"
            rounded="md"
            alignItems="flex-start"
        >
            <HStack alignItems="flex-start" spacing={3} w="full">
                <AlertIcon mt={1} />
                <VStack alignItems="flex-start" spacing={1} flex={1}>
                    <AlertTitle fontSize="sm" lineHeight="short">
                        {title}
                    </AlertTitle>
                    <AlertDescription fontSize="sm" lineHeight="short">
                        {description}
                    </AlertDescription>
                    {(primaryAction || secondaryAction) && (
                        <Box pt={1}>
                            <ActionLinks
                                primaryAction={primaryAction}
                                secondaryAction={secondaryAction}
                            />
                        </Box>
                    )}
                </VStack>
            </HStack>
        </Alert>
    )
}

function ActionLinks({
    primaryAction,
    secondaryAction,
}: {
    primaryAction?: AuthErrorAction
    secondaryAction?: AuthErrorAction
}) {
    return (
        <HStack spacing={3} flexWrap="wrap">
            {primaryAction && <ActionLink action={primaryAction} isPrimary />}
            {primaryAction && secondaryAction && (
                <Text color="red.300" fontSize="sm" aria-hidden>
                    •
                </Text>
            )}
            {secondaryAction && <ActionLink action={secondaryAction} />}
        </HStack>
    )
}

function ActionLink({
    action,
    isPrimary,
}: {
    action: AuthErrorAction
    isPrimary?: boolean
}) {
    const fontWeight = isPrimary ? 700 : 600
    const color = 'red.700'
    const fontSize = 'sm'

    if (action.mailto) {
        return (
            <Text
                as="a"
                href={`mailto:${action.mailto}`}
                color={color}
                fontWeight={fontWeight}
                fontSize={fontSize}
                textDecoration="underline"
            >
                {action.label}
            </Text>
        )
    }

    if (action.href) {
        return (
            <Text
                as={GatsbyLink}
                to={action.href}
                color={color}
                fontWeight={fontWeight}
                fontSize={fontSize}
                textDecoration="underline"
            >
                {action.label}
            </Text>
        )
    }

    return null
}
